import { useState, useEffect } from 'react'
import { Send, Settings, Copy, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { EVENT_TEMPLATES, getEventTemplate } from '../utils/sampleData'
import type { CustomEvent, CustomEventResponse, ApiCredentials } from '../types/ingestion.types'
import { INGESTION_API } from '@/api/client'

export function SingleEventTester() {
  const [credentials, setCredentials] = useState<ApiCredentials | null>(null)
  const [showCredentialsForm, setShowCredentialsForm] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<string>('page_view')
  const [eventData, setEventData] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [response, setResponse] = useState<CustomEventResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // Load credentials from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('fh_api_credentials')
    if (stored) {
      try {
        setCredentials(JSON.parse(stored))
      } catch {
        // Invalid JSON, ignore
      }
    } else {
      setShowCredentialsForm(true)
    }
  }, [])

  // Update event data when template changes
  useEffect(() => {
    const template = getEventTemplate(selectedTemplate, credentials?.accountId)
    setEventData(JSON.stringify(template, null, 2))
  }, [selectedTemplate, credentials])

  const handleSaveCredentials = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const creds: ApiCredentials = {
      keyId: formData.get('keyId') as string,
      secret: formData.get('secret') as string,
      accountId: parseInt(formData.get('accountId') as string, 10),
    }
    localStorage.setItem('fh_api_credentials', JSON.stringify(creds))
    setCredentials(creds)
    setShowCredentialsForm(false)
  }

  const handleSendEvent = async () => {
    if (!credentials) {
      setError('Please configure API credentials first')
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      setResponse(null)

      const event: CustomEvent = JSON.parse(eventData)

      const res = await fetch(`${INGESTION_API}/events/custom`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-FH-Key-ID': credentials.keyId,
          'X-FH-Key-Secret': credentials.secret,
        },
        body: JSON.stringify(event),
      })

      const data = await res.json()

      if (!res.ok) {
        throw new Error(data.message || `HTTP ${res.status}: ${res.statusText}`)
      }

      setResponse(data)

      const historyItem = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        method: 'POST' as const,
        endpoint: '/api/v1/events/custom',
        status: res.status,
        duration: 0,
        request: event,
        response: data,
      }
      const history = JSON.parse(localStorage.getItem('fh_request_history') || '[]')
      history.unshift(historyItem)
      localStorage.setItem('fh_request_history', JSON.stringify(history.slice(0, 50)))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(eventData)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (showCredentialsForm || !credentials) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-blue-900 mb-2">Configure API Credentials</h3>
          <p className="text-sm text-blue-700">
            Enter your Ingestion API credentials to start testing. These will be stored locally in your browser.
          </p>
        </div>

        <form onSubmit={handleSaveCredentials} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Account ID</label>
            <input
              type="number"
              name="accountId"
              required
              defaultValue={credentials?.accountId}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="1234"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">API Key ID</label>
            <input
              type="text"
              name="keyId"
              required
              defaultValue={credentials?.keyId}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
              placeholder="fh_live_xxxx"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">API Secret</label>
            <input
              type="password"
              name="secret"
              required
              defaultValue={credentials?.secret}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
              placeholder="sk_xxxx"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              className="flex-1 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
            >
              Save Credentials
            </button>
            {credentials && (
              <button
                type="button"
                onClick={() => setShowCredentialsForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Credentials Badge */}
      <div className="flex items-center gap-3">
        <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium font-mono">
          {credentials.keyId}
        </div>
        <button
          onClick={() => setShowCredentialsForm(true)}
          className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
        >
          <Settings className="h-4 w-4" />
          Update Credentials
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Event Builder */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Event Template</label>
            <select
              value={selectedTemplate}
              onChange={e => setSelectedTemplate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {Object.entries(EVENT_TEMPLATES).map(([key, template]) => (
                <option key={key} value={key}>{template.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {EVENT_TEMPLATES[selectedTemplate]?.description}
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Event JSON</label>
              <button
                onClick={handleCopy}
                className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
              >
                {copied ? (
                  <><Check className="h-4 w-4 text-green-600" /><span className="text-green-600">Copied!</span></>
                ) : (
                  <><Copy className="h-4 w-4" />Copy</>
                )}
              </button>
            </div>
            <textarea
              value={eventData}
              onChange={e => setEventData(e.target.value)}
              className="w-full h-96 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
              placeholder="Enter event JSON here..."
            />
          </div>

          <button
            onClick={handleSendEvent}
            disabled={isLoading || !eventData}
            className={cn(
              'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md font-medium transition-colors',
              isLoading || !eventData
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary/90'
            )}
          >
            <Send className="h-5 w-5" />
            {isLoading ? 'Sending...' : 'Send Event'}
          </button>
        </div>

        {/* Right Column - Response */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">Response</label>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-red-600 font-medium">Error</div>
              <pre className="mt-2 text-sm text-red-800 whitespace-pre-wrap font-mono">{error}</pre>
            </div>
          )}

          {response && (
            <div className={cn(
              'border rounded-lg p-4',
              response.status === 'SUCCESS' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
            )}>
              <div className={cn(
                'px-2 py-1 rounded text-sm font-medium inline-block mb-3',
                response.status === 'SUCCESS' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              )}>
                {response.status}
              </div>
              <pre className="text-sm whitespace-pre-wrap font-mono">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>
          )}

          {!error && !response && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
              <Send className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">Send an event to see the response</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
